import { ClipboardList, Loader2, Search } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGuardContext } from '@/hooks/useGuardContext';
import { useTranslation } from '@/hooks/useTranslation';
import { getGuardDailyLog } from '@/services/api.client';
import type { AccessEvent, InvitationType } from '@/types';

// ─── Status badge config ─────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, { bg: string; text: string; labelKey: string }> = {
  GRANTED: {
    bg: 'bg-[var(--status-green-tint)]',
    text: 'text-[var(--status-green-text)]',
    labelKey: 'guard.activity.statusGranted',
  },
  DENIED: {
    bg: 'bg-[var(--status-red-tint)]',
    text: 'text-[var(--status-red-text)]',
    labelKey: 'guard.activity.statusDenied',
  },
  WALK_IN: {
    bg: 'bg-[var(--status-purple-tint)]',
    text: 'text-[var(--status-purple-text)]',
    labelKey: 'guard.activity.statusWalkIn',
  },
  ALERT: {
    bg: 'bg-[var(--status-amber-tint)]',
    text: 'text-[var(--status-amber-text)]',
    labelKey: 'guard.activity.statusAlert',
  },
};

const INVITATION_LABELS: Record<InvitationType, string> = {
  single_visit: 'guard.queue.chipInvited',
  recurring: 'guard.queue.chipRecurring',
  domestic_worker: 'guard.queue.chipRecurring',
  delivery: 'guard.queue.chipDelivery',
  taxi: 'guard.queue.chipTaxi',
  walk_in: 'guard.queue.chipWalkIn',
  employee: 'guard.queue.chipEmployee',
  event: 'guard.queue.chipInvited',
  trusted_operator: 'Trusted Operator',
};

function formatTime(isoString: string): string {
  try {
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoString;
  }
}

/**
 * Activity Log — Today's access events for the guard's gate.
 * Requirements: 6.1–6.5
 */
export function ActivityLogPage() {
  const { t } = useTranslation();
  const { gateId } = useGuardContext();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  }, []);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    useInfiniteQuery({
      queryKey: ['guard', 'daily-log', gateId, debouncedSearch],
      queryFn: ({ pageParam }) =>
        getGuardDailyLog({
          ...(gateId ? { gateId } : {}),
          ...(pageParam ? { cursor: pageParam as string } : {}),
          limit: 25,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) =>
        lastPage.meta.hasMore ? (lastPage.meta.nextCursor ?? undefined) : undefined,
    });

  const events = data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <ClipboardList className="size-6 text-[var(--text-accent)]" />
        <h1 className="text-xl text-[var(--text-primary)]">{t('guard.activity.title')}</h1>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--text-tertiary)]" />
        <Input
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={t('guard.activity.searchPlaceholder')}
          className="pl-10 min-h-[44px]"
          aria-label={t('guard.activity.searchPlaceholder')}
        />
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-[var(--text-secondary)]" />
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-error)] bg-[var(--status-red-tint)] p-4">
          <p className="text-sm text-[var(--text-error)]">
            {(error as { message?: string })?.message ?? t('common.error')}
          </p>
        </div>
      )}

      {!isLoading && !error && events.length === 0 && (
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-8 text-center">
          <p className="text-sm text-[var(--text-secondary)]">{t('guard.activity.empty')}</p>
        </div>
      )}

      {events.length > 0 && (
        <div className="space-y-2">
          {events.map((event) => (
            <EventRow key={event.id} event={event} />
          ))}
        </div>
      )}

      {hasNextPage && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            className="min-h-[44px]"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('guard.activity.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Event Row ───────────────────────────────────────────────────────────────

function EventRow({ event }: { event: AccessEvent }) {
  const { t } = useTranslation();
  const statusConfig = STATUS_STYLES[event.status];
  const statusStyle = statusConfig ?? {
    bg: 'bg-muted',
    text: 'text-muted-foreground',
    labelKey: 'guard.activity.statusGranted',
  };
  const invitationLabel = INVITATION_LABELS[event.invitationType] ?? 'guard.queue.chipInvited';

  return (
    <div className="flex items-center gap-3 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 transition-colors hover:bg-[var(--bg-surface-raised)]">
      <span
        className={`inline-flex items-center rounded-[var(--radius-md)] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusStyle.bg} ${statusStyle.text}`}
      >
        {t(statusStyle.labelKey)}
      </span>

      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <span className="text-sm font-medium text-[var(--text-primary)] truncate">
          {event.visitorName}
        </span>
        <span className="text-xs text-[var(--text-secondary)] truncate">{event.hostUnit}</span>
      </div>

      <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
        {t(invitationLabel)}
      </span>

      <span className="text-xs text-[var(--text-secondary)] tabular-nums whitespace-nowrap">
        {formatTime(event.timestamp)}
      </span>
    </div>
  );
}
